import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { AppFooter } from "@/components/AppFooter";
import { useSettings } from "@/contexts/SettingsContext";

export default function Privacy() {
  const { settings } = useSettings();
  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="privacy-page">
      <main className="flex-1 px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-home">
            <ArrowLeft size={16} /> Back home
          </Link>
          <header className="mt-6">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Legal</p>
            <h1 className="text-4xl sm:text-5xl font-bold mt-2">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mt-3">Last updated: February 2026</p>
          </header>

          <section className="prose prose-sm max-w-none mt-10 space-y-6 text-base leading-relaxed">
            <p>
              {settings.app_name || "My NutriTrack"} ("we", "us", "our") is a private nutrition-management
              service operated by <b>Dt. Dana Shakaa</b>. This Privacy Policy describes how we collect,
              use, and protect your personal information when you use our application.
            </p>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">1. Information we collect</h2>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li><b>Account data</b> &mdash; name, email, mobile number, patient number, and a hashed password.</li>
                <li><b>Health profile</b> &mdash; age, gender, height, weight, allergies, medical conditions, food preferences, and goals you choose to share with your dietitian.</li>
                <li><b>Activity</b> &mdash; meal logs, water intake, login sessions, and photos you upload.</li>
                <li><b>Technical data</b> &mdash; browser, device, IP address (used only for security and abuse prevention).</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">2. How we use your information</h2>
              <p>We use your information solely to:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Provide you with a personalized nutrition plan and track your progress.</li>
                <li>Allow your dietitian to monitor compliance and adjust your plan.</li>
                <li>Send transactional emails (verification codes, password resets, welcome messages).</li>
                <li>Keep the service secure and prevent unauthorized access.</li>
              </ul>
              <p className="mt-3">
                <b>We never sell or rent your personal data.</b> We do not show third-party advertising.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">3. Who can see your data</h2>
              <p>
                Your health profile and activity are visible only to <b>you</b> and the <b>dietitian assigned to you</b>.
                Service operators may access aggregated, anonymized data for service maintenance.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">4. Service providers</h2>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li><b>Resend</b> &mdash; transactional email delivery.</li>
                <li><b>USDA FoodData Central</b> &mdash; public food/nutrient lookup (no personal data shared).</li>
                <li><b>Cloud hosting</b> &mdash; encrypted storage of your data and uploaded photos.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">5. Security</h2>
              <p>
                Passwords are hashed with bcrypt and never stored in plain text. All traffic is encrypted with TLS.
                Authentication uses signed JWT tokens. We follow industry best practices to protect your data.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">6. Your rights</h2>
              <p>You can at any time:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>Request a copy of your data.</li>
                <li>Ask us to correct inaccurate information.</li>
                <li>Request deletion of your account and all associated data.</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{" "}
                <a href="mailto:info@mynutritrack.app" className="text-primary hover:underline">info@mynutritrack.app</a>{" "}
                or via the <Link to="/contact" className="text-primary hover:underline">contact form</Link>.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">7. Data retention</h2>
              <p>
                We retain your data for as long as your account is active. If you delete your account, we will
                remove your personal data within 30 days, except where law requires us to retain certain records.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">8. Changes to this policy</h2>
              <p>
                We may update this policy from time to time. The "Last updated" date at the top of this page reflects
                the most recent revision. Significant changes will be communicated via email.
              </p>
            </div>

            <div>
              <h2 className="text-lg font-semibold mt-6 mb-2">9. Contact</h2>
              <p>
                Questions about this policy? Email{" "}
                <a href="mailto:info@mynutritrack.app" className="text-primary hover:underline">info@mynutritrack.app</a>.
              </p>
            </div>
          </section>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
